import { useState } from "react";
import useSWR, { mutate } from "swr";
import { useTranslation } from "react-i18next";
import { fetcher } from "@/lib/api";
import client from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TraceabilityDrawer from "./TraceabilityDrawer";
import { useAuth } from "@/contexts/AuthContext";

export default function PegadaFactores() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { data } = useSWR("/carbon/factors", fetcher);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const lang = i18n.language;
  const isAdmin = user?.role === "ADMIN";

  const factors = data?.factors || [];

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await client.delete(`/carbon/factors/${confirmDelete.code}`);
      toast.success(t("fase3.factor_deleted", { code: confirmDelete.code }));
      setConfirmDelete(null);
      mutate("/carbon/factors");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(t("fase3.factor_delete_409"));
      } else if (status === 404) {
        toast.error(t("fase3.factor_delete_404"));
      } else {
        toast.error(t("fase3.factor_delete_error"));
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="pegada-factores">
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-salnes-sand/40 text-salnes-stone">
            <tr>
              <Th>{t("fase3.factor")}</Th>
              <Th>{t("fase3.category")}</Th>
              <Th className="text-center">{t("fase3.scope")}</Th>
              <Th className="text-right">{t("fase3.value")}</Th>
              <Th>{t("fase3.unit")}</Th>
              <Th>{t("fase3.source")}</Th>
              <Th className="text-center">{t("fase3.version")}</Th>
              {isAdmin && <Th className="text-center w-10">&nbsp;</Th>}
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {factors.map((f) => (
              <tr
                key={f.code}
                data-testid={`factor-row-${f.code}`}
                className="hover:bg-salnes-sand/20 cursor-pointer transition-colors"
                onClick={() =>
                  setSelected({
                    formula: `${f.unit_output} = <${f.unit_input}> × ${f.value_kgco2e_per_unit}`,
                    factor: f,
                    inputs: { code: f.code, name: f[`name_${lang}`], notes: f[`notes_${lang}`] },
                    computed_at: f.updated_at,
                  })
                }
              >
                <Td>
                  <div className="font-medium text-salnes-ink">{f[`name_${lang}`] || f.name_gl}</div>
                  <div className="text-[11px] text-salnes-stone">{f.code}</div>
                </Td>
                <Td className="capitalize">{f.category}</Td>
                <Td className="text-center">
                  <Badge className="bg-salnes-atlantic text-white text-[10px]">{f.scope}</Badge>
                </Td>
                <Td className="text-right tabular-nums font-medium">{f.value_kgco2e_per_unit}</Td>
                <Td>kg CO₂e / {f.unit_input}</Td>
                <Td>
                  <div className="text-[11px] text-salnes-stone">{f.source_name}</div>
                  {f.source_url && (
                    <a
                      href={f.source_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[11px] text-salnes-atlantic inline-flex items-center gap-1"
                    >
                      <ExternalLink size={11} /> URL
                    </a>
                  )}
                </Td>
                <Td className="text-center text-xs">v{f.version}</Td>
                {isAdmin && (
                  <Td className="text-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(f);
                      }}
                      data-testid={`factor-delete-btn-${f.code}`}
                      aria-label={t("fase3.factor_delete_aria", { code: f.code })}
                      title={t("fase3.factor_delete_aria", { code: f.code })}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-md text-salnes-stone hover:text-salnes-coral hover:bg-salnes-coral/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TraceabilityDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        data={selected}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && !deleting && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="factor-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("fase3.factor_delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("fase3.factor_delete_body", { code: confirmDelete?.code || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="factor-delete-cancel">
              {t("fase3.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              data-testid="factor-delete-confirm"
              className="bg-salnes-coral hover:bg-salnes-coral/90 text-white"
            >
              {deleting ? (
                <Loader2 size={13} className="mr-2 animate-spin" />
              ) : (
                <Trash2 size={13} className="mr-2" />
              )}
              {t("fase3.factor_delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-[10px] uppercase tracking-[0.18em] font-medium px-4 py-3 ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
